"use client";
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { KPITargetItem } from "@/modules/strategy/ui/targets/KPITargetForm";
import { CommitmentLevel } from "@/modules/strategy/ui/targets/ResourceCommitmentSelector";

// Shortlisted pathway type
export interface ShortlistedPathway {
    id: string;
    title: string;
    description: string;
    likelyImpact: number;
    complexity: number;
    timeframe: string;
    opportunityTitle: string; // Parent opportunity name for display
}

// Pinned initiative for later blueprint development
export interface PinnedInitiative {
    id: string;
    title: string;
    description: string;
    opportunityTitle: string;
    pinnedAt: string;
    analysisData?: any; // Store analysis data for later use
}

// Yearly impact breakdown for financial projections
export interface YearlyImpact {
    year: string; // FY26, FY27, etc.
    revenueImpact: number;
    ebitdaImpact: number;
    capex?: number;
    opex?: number;
    netCashflow?: number;
    assumptions?: string[];
}

// Initiative Analysis data (from 2x2 matrix page)
export interface InitiativeAnalysisData {
    id: string;
    title: string;
    status: 'pending' | 'in_process' | 'completed' | 'user_edited';
    revenueUpside?: { value: string; assumptions: string[]; logic: string };
    ebitdaUpside?: { value: string; assumptions: string[]; logic: string };
    budget?: { 
        total: string; 
        breakdown: Array<{item: string; amount: string}>; 
        fteCount: number; 
        keyHires: Array<{role: string; rationale: string; estimatedCost: string}>; 
        systems: string[];
        totalCapex?: string;
        totalOpex?: string;
    };
    irr?: { percentage: number; discountRate: number; npvCalculation: string; assumptions: string[] };
    timeline?: { duration: string; milestones: Array<{phase: string; weeks: string}> };
    keyConsiderations?: { risks: string[]; dependencies: string[]; opportunities: string[] };
    businessValue: number;
    executionComplexity: number;
    confidenceLevel?: string;
    supervisorApproved?: boolean;
    supervisorNotes?: string;
    // Financial projections by year
    yearlyImpacts?: YearlyImpact[];
}

// Define the shape of our Strategy State
interface StrategyState {
    selectedInitiative: string;
    commitmentLevel: CommitmentLevel;
    leadingKPIs: KPITargetItem[];
    laggingKPIs: KPITargetItem[];
    // Global App State
    isDataIngested: boolean;
    activeInitiatives: any[];
    soapData: SoapData;
    shortlistedPathways: ShortlistedPathway[];
    selectedPathwayIds: string[]; // Only checked items in Matrix move to Blueprint
    selectedForBlueprint: string | null; // Single initiative selected for blueprint (radio selection)
    pinnedInitiatives: PinnedInitiative[]; // Initiatives pinned for later
    blueprintGaps: any[]; // Stores AI generated execution data
    dashboardIntelligence: any | null; // Stores AI generated dashboard signals & metrics
    pinnedInsightIds: string[];
    deletedInsightIds: string[];
    // Initiative analysis data (single source of truth from matrix page)
    initiativeAnalyses: Record<string, InitiativeAnalysisData>;
}

export interface SoapData {
    strategicAmbition: string; // Primary: unified vision + mission
    vision: string;  // Legacy/backup
    mission: string; // Legacy/backup
    niche: string;
    coreStrategy: string;
    enablers: string[];
    gaps: string[];
}

// Define the Actions (setters)
interface StrategyContextType extends StrategyState {
    setSelectedInitiative: (name: string) => void;
    setCommitmentLevel: (level: CommitmentLevel) => void;
    setLeadingKPIs: (kpis: KPITargetItem[]) => void;
    setLaggingKPIs: (kpis: KPITargetItem[]) => void;
    updateLeadingKPI: (gapId: string, index: number, field: string, value: string) => void;
    updateLaggingKPI: (gapId: string, index: number, field: string, value: string) => void;
    // SOAP Actions
    updateSoapData: (field: keyof SoapData, value: string | string[]) => void;
    generateSoap: (foundationData: any) => Promise<void>;
    isGeneratingSoap: boolean;
    // Blueprint Actions
    generateBlueprint: (initiative: any) => Promise<any>;
    // Global Actions
    setDataIngested: (status: boolean) => void;
    addActiveInitiative: (initiative: any) => void;
    removeActiveInitiative: (id: string) => void;
    // Shortlisting Actions
    toggleShortlistedPathway: (pathway: ShortlistedPathway) => void;
    isPathwayShortlisted: (pathwayId: string) => boolean;
    clearShortlistedPathways: () => void;
    updateShortlistedPathwayScores: (id: string, businessValue: number, complexity: number) => void;
    setSelectedPathwayIds: (ids: string[]) => void;
    setBlueprintGaps: (gaps: any[]) => void;
    // Single selection for blueprint
    selectedForBlueprint: string | null;
    setSelectedForBlueprint: (id: string | null) => void;
    // Pinned initiatives
    pinnedInitiatives: PinnedInitiative[];
    pinInitiative: (initiative: PinnedInitiative) => void;
    unpinInitiative: (id: string) => void;
    isInitiativePinned: (id: string) => boolean;
    generateDashboardInsights: (foundationData: any, isDeepSearch?: boolean, structuredFinancials?: any, formData?: any) => Promise<void>;
    isGeneratingIntelligence: boolean;
    setDashboardIntelligence: (intelligence: any) => void;
    togglePinInsight: (id: string) => void;
    deleteInsight: (id: string) => void;
    // Initiative analyses (single source of truth)
    initiativeAnalyses: Record<string, InitiativeAnalysisData>;
    setInitiativeAnalysis: (id: string, analysis: InitiativeAnalysisData) => void;
    setInitiativeAnalyses: (analyses: Record<string, InitiativeAnalysisData>) => void;
    getInitiativeAnalysis: (id: string) => InitiativeAnalysisData | undefined;
}

const StrategyContext = createContext<StrategyContextType | undefined>(undefined);

// Default Mock Data (moved from individual pages to here)
const INITIAL_LEADING_KPIS: KPITargetItem[] = [];

const INITIAL_LAGGING_KPIS: KPITargetItem[] = [];

import { useAuth } from "@/context/AuthContext";

// ... existing interfaces ...

export const StrategyProvider = ({ children }: { children: ReactNode }) => {
    const { activeCompany, user } = useAuth();
    const [selectedInitiative, setSelectedInitiative] = useState("Sales process optimization");
    const [commitmentLevel, setCommitmentLevel] = useState<CommitmentLevel>("medium");
    const [leadingKPIs, setLeadingKPIs] = useState<KPITargetItem[]>(INITIAL_LEADING_KPIS);
    const [laggingKPIs, setLaggingKPIs] = useState<KPITargetItem[]>(INITIAL_LAGGING_KPIS);

    // Global App State
    const [isDataIngested, setIsDataIngested] = useState(false);
    const [activeInitiatives, setActiveInitiatives] = useState<any[]>([]);
    const [selectedPathwayIds, setSelectedPathwayIds] = useState<string[]>([]);
    const [blueprintGaps, setBlueprintGaps] = useState<any[]>([]);
    const [dashboardIntelligence, setDashboardIntelligence] = useState<any | null>(null);
    const [pinnedInsightIds, setPinnedInsightIds] = useState<string[]>([]);
    const [deletedInsightIds, setDeletedInsightIds] = useState<string[]>([]);
    const [isGeneratingIntelligence, setIsGeneratingIntelligence] = useState(false);

    // Shortlisted pathways
    const [shortlistedPathways, setShortlistedPathways] = useState<ShortlistedPathway[]>([]);
    const [selectedForBlueprint, setSelectedForBlueprint] = useState<string | null>(null);
    const [pinnedInitiatives, setPinnedInitiatives] = useState<PinnedInitiative[]>([]);
    const [soapData, setSoapData] = useState<SoapData>({
        strategicAmbition: "", vision: "", mission: "", niche: "", coreStrategy: "", enablers: [], gaps: []
    });
    const [isHydrated, setIsHydrated] = useState(false);
    const [isCloudHydrated, setIsCloudHydrated] = useState(false);
    const cloudSyncTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    
    // Initiative analyses - SINGLE SOURCE OF TRUTH for all financial/business data
    const [initiativeAnalyses, setInitiativeAnalysesState] = useState<Record<string, InitiativeAnalysisData>>({});

    // Initial Load - Depend on activeCompany.id
    useEffect(() => {
        setIsHydrated(false);
        setIsCloudHydrated(false);

        // Reset all state to defaults before cloud hydration
        setIsDataIngested(false);
        setActiveInitiatives([]);
        setShortlistedPathways([]);
        setSoapData({ strategicAmbition: "", vision: "", mission: "", niche: "", coreStrategy: "", enablers: [], gaps: [] });
        setSelectedPathwayIds([]);
        setBlueprintGaps([]);
        setDashboardIntelligence(null);
        setPinnedInsightIds([]);
        setDeletedInsightIds([]);
        setPinnedInitiatives([]);
        setSelectedForBlueprint(null);
        setInitiativeAnalysesState({});

        const hydrateCloud = async () => {
            if (!user?.id || !activeCompany?.id) return;
            try {
                const res = await fetch(
                    `/api/cloud/company-blobs?companyId=${encodeURIComponent(activeCompany.id)}&blobType=strategy_state`
                );
                if (!res.ok) return;
                const cloud = await res.json();
                if (cloud?.success && cloud?.payload) {
                    const p = cloud.payload;
                    if (typeof p.isDataIngested === "boolean") setIsDataIngested(p.isDataIngested);
                    if (Array.isArray(p.activeInitiatives)) setActiveInitiatives(p.activeInitiatives);
                    if (Array.isArray(p.shortlistedPathways)) setShortlistedPathways(p.shortlistedPathways);
                    if (p.soapData) setSoapData(p.soapData);
                    if (Array.isArray(p.selectedPathwayIds)) setSelectedPathwayIds(p.selectedPathwayIds);
                    if (Array.isArray(p.blueprintGaps)) setBlueprintGaps(p.blueprintGaps);
                    if (p.dashboardIntelligence !== undefined) setDashboardIntelligence(p.dashboardIntelligence);
                    if (Array.isArray(p.pinnedInsightIds)) setPinnedInsightIds(p.pinnedInsightIds);
                    if (Array.isArray(p.deletedInsightIds)) setDeletedInsightIds(p.deletedInsightIds);
                    if (Array.isArray(p.pinnedInitiatives)) setPinnedInitiatives(p.pinnedInitiatives);
                    if (p.selectedForBlueprint !== undefined) setSelectedForBlueprint(p.selectedForBlueprint);
                    if (p.initiativeAnalyses && typeof p.initiativeAnalyses === "object") setInitiativeAnalysesState(p.initiativeAnalyses);
                    console.log("☁️ Hydrated Strategy state from cloud");
                }
            } catch (err) {
                console.warn("Cloud hydrate failed for strategy state:", err);
            }
        };

        hydrateCloud().finally(() => {
            setIsHydrated(true);
            setIsCloudHydrated(true);
        });
    }, [activeCompany?.id, user?.id]);

    // Cloud sync for strategy state
    useEffect(() => {
        if (!isHydrated || !isCloudHydrated || !activeCompany?.id || !user?.id) return;

        if (cloudSyncTimeoutRef.current) clearTimeout(cloudSyncTimeoutRef.current);
        cloudSyncTimeoutRef.current = setTimeout(async () => {
            try {
                await fetch("/api/cloud/company-blobs", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        companyId: activeCompany.id,
                        blobType: "strategy_state",
                        payload: {
                            isDataIngested,
                            activeInitiatives,
                            shortlistedPathways,
                            soapData,
                            selectedPathwayIds,
                            blueprintGaps,
                            dashboardIntelligence,
                            pinnedInsightIds,
                            deletedInsightIds,
                            pinnedInitiatives,
                            selectedForBlueprint,
                            initiativeAnalyses,
                        },
                    }),
                });
            } catch (err) {
                console.warn("Cloud sync failed for strategy state:", err);
            }
        }, 600);

        return () => {
            if (cloudSyncTimeoutRef.current) clearTimeout(cloudSyncTimeoutRef.current);
        };
    }, [
        user?.id,
        activeCompany?.id,
        isHydrated,
        isCloudHydrated,
        isDataIngested,
        activeInitiatives,
        shortlistedPathways,
        soapData,
        selectedPathwayIds,
        blueprintGaps,
        dashboardIntelligence,
        pinnedInsightIds,
        deletedInsightIds,
        pinnedInitiatives,
        selectedForBlueprint,
        initiativeAnalyses,
    ]);

    // Data Reconciliation Logic: Ensure selected IDs are always a subset of shortlisted pathways
    useEffect(() => {
        if (!isHydrated) return;
        const shortlistedIds = new Set(shortlistedPathways.map(p => p.id));
        const validSelectedIds = selectedPathwayIds.filter(id => shortlistedIds.has(id));

        if (validSelectedIds.length !== selectedPathwayIds.length) {
            setSelectedPathwayIds(validSelectedIds);
        }

        // ═══════════════════════════════════════════════════════════════════════════
        // CRITICAL DATA INTEGRITY FIX:
        // Reconcile blueprintGaps to ONLY include:
        // 1. The initiative selected via selectedForBlueprint (new single-selection flow)
        // 2. OR initiatives from the old multi-select flow (selectedPathwayIds) for backward compatibility
        // 3. OR active initiatives already finalized
        // ═══════════════════════════════════════════════════════════════════════════
        
        const validTitles = new Set<string>();
        
        // Add title for single-selection flow (new)
        if (selectedForBlueprint) {
            const selectedPathway = shortlistedPathways.find(p => p.id === selectedForBlueprint);
            if (selectedPathway) {
                validTitles.add(selectedPathway.opportunityTitle);
                console.log(`📋 [StrategyContext] Valid title from selectedForBlueprint: "${selectedPathway.opportunityTitle}"`);
            }
        }
        
        // Add titles from multi-select flow (backward compatibility)
        shortlistedPathways
            .filter(p => selectedPathwayIds.includes(p.id))
            .forEach(p => validTitles.add(p.opportunityTitle));
        
        // Add titles from finalized active initiatives
        activeInitiatives.forEach(i => validTitles.add(i.title));
        
        console.log(`📋 [StrategyContext] Reconciling blueprintGaps. Valid titles:`, Array.from(validTitles));
        console.log(`📋 [StrategyContext] Current blueprintGaps:`, blueprintGaps.map(g => g.gapTitle));

        const filteredGaps = blueprintGaps.filter(gap => validTitles.has(gap.gapTitle));
        
        if (filteredGaps.length !== blueprintGaps.length) {
            console.log(`📋 [StrategyContext] Removing ${blueprintGaps.length - filteredGaps.length} stale gaps`);
            setBlueprintGaps(filteredGaps);
        }

    }, [shortlistedPathways, selectedPathwayIds, selectedForBlueprint, activeInitiatives, isHydrated, blueprintGaps]);

    const updateLeadingKPI = (gapId: string, index: number, field: string, value: string) => {
        setBlueprintGaps(prev => prev.map(gap => {
            if (gap.gapId === gapId) {
                const updatedKPIs = [...(gap.kpis || [])];
                const leadingKpis = updatedKPIs.filter((k: any) => k.type?.toLowerCase() === 'leading');
                if (leadingKpis[index]) {
                    const originalKpi = leadingKpis[index];
                    const kpiToUpdate = updatedKPIs.find(k => k.title === originalKpi.title);
                    if (kpiToUpdate) {
                        kpiToUpdate[field] = value;
                    }
                }
                return { ...gap, kpis: updatedKPIs };
            }
            return gap;
        }));
    };

    const updateLaggingKPI = (gapId: string, index: number, field: string, value: string) => {
        setBlueprintGaps(prev => prev.map(gap => {
            if (gap.gapId === gapId) {
                const updatedKPIs = [...(gap.kpis || [])];
                const laggingKpis = updatedKPIs.filter((k: any) => k.type?.toLowerCase() === 'lagging');
                if (laggingKpis[index]) {
                    const originalKpi = laggingKpis[index];
                    const kpiToUpdate = updatedKPIs.find(k => k.title === originalKpi.title);
                    if (kpiToUpdate) {
                        kpiToUpdate[field] = value;
                    }
                }
                return { ...gap, kpis: updatedKPIs };
            }
            return gap;
        }));
    };

    const setDataIngested = (status: boolean) => setIsDataIngested(status);

    const addActiveInitiative = (initiative: any) => {
        setActiveInitiatives(prev => [...prev, initiative]);
    };

    const removeActiveInitiative = (id: string) => {
        const initiativeToRemove = activeInitiatives.find(i => i.id === id);
        if (initiativeToRemove) {
            // Also clean up blueprints and selections to sync waterfall
            setBlueprintGaps(prev => prev.filter(gap => gap.gapTitle !== initiativeToRemove.title));

            // Reconcile selectedPathwayIds - remove pathways belonging to this gap
            const associatedPathwayIds = shortlistedPathways
                .filter(p => p.opportunityTitle === initiativeToRemove.title)
                .map(p => p.id);

            if (associatedPathwayIds.length > 0) {
                setSelectedPathwayIds(prev => prev.filter(pid => !associatedPathwayIds.includes(pid)));
            }
        }
        setActiveInitiatives(prev => prev.filter(i => i.id !== id));
    };

    const toggleShortlistedPathway = (pathway: ShortlistedPathway) => {
        setShortlistedPathways(prev => {
            const exists = prev.find(p => p.id === pathway.id);
            if (exists) {
                // When removing from shortlist, also remove from selection
                setSelectedPathwayIds(sPrev => sPrev.filter(id => id !== pathway.id));
                return prev.filter(p => p.id !== pathway.id);
            }
            return [...prev, pathway];
        });
    };

    const isPathwayShortlisted = (pathwayId: string) => {
        return shortlistedPathways.some(p => p.id === pathwayId);
    };

    const clearShortlistedPathways = () => {
        setShortlistedPathways([]);
    };

    const updateShortlistedPathwayScores = (id: string, businessValue: number, complexity: number) => {
        setShortlistedPathways(prev => prev.map(p =>
            p.id === id ? { ...p, likelyImpact: businessValue, complexity: complexity } : p
        ));
    };

    const [isGeneratingSoap, setIsGeneratingSoap] = useState(false);

    const updateSoapData = (field: keyof SoapData, value: string | string[]) => {
        console.log(`Updating soapData.${field}:`, value);
        setSoapData(prev => ({ ...prev, [field]: value }));
    };

    const generateSoap = async (inputData: any) => {
        setIsGeneratingSoap(true);
        try {
            // Normalize input data - handle both formData (from wizard) and foundationData (direct)
            // formData has structure: { foundation: { missionStatement, visionStatement, ... }, ... }
            // foundationData has structure: { missionStatement, visionStatement, ... }
            const foundationData = inputData.foundation 
                ? {
                    // If inputData has .foundation, extract and flatten it
                    ...inputData.foundation,
                    // Also include other top-level fields that might be useful
                    ...(inputData.financials ? { financials: inputData.financials } : {}),
                    ...(inputData.market ? { market: inputData.market } : {}),
                    ...(inputData.product ? { product: inputData.product } : {}),
                    ...(inputData.gtm ? { gtm: inputData.gtm } : {}),
                    ...(inputData.org ? { org: inputData.org } : {})
                  }
                : inputData; // If no .foundation, assume it's already foundationData

            // Get company config for data quality screening
            const companyConfig = activeCompany ? {
                reportingUnit: activeCompany.reportingUnit || 'M',
                reportingCurrency: activeCompany.reportingCurrency || 'USD'
            } : undefined;

            console.log("Calling SOAP API with foundation:", foundationData);
            const response = await fetch("/api/strategy/generate-soap", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ foundationData, companyConfig })
            });

            if (!response.ok) throw new Error("SOAP Generation Failed");

            const result = await response.json();
            const soap = result.data;

            // Priority order for strategic ambition:
            // 1. AI-generated unified strategicAmbition (best - single cohesive statement)
            // 2. Wizard data if they entered a strategicAmbition
            // 3. Fallback to vision + mission (legacy)
            const strategicAmbition = soap.strategicAmbition?.trim() ||
                                     foundationData.strategicAmbition?.trim() ||
                                     soapData.strategicAmbition?.trim() ||
                                     "";
            
            // Legacy fields for backward compatibility
            const mission = foundationData.missionStatement?.trim() || 
                          soap.missionStatement?.trim() || 
                          soapData.mission?.trim() || 
                          "";
            
            const vision = foundationData.visionStatement?.trim() || 
                         soap.visionStatement?.trim() || 
                         soapData.vision?.trim() || 
                         "";

            // Map AI SOAP to State
            setSoapData({
                strategicAmbition: strategicAmbition,
                vision: vision,
                mission: mission,
                niche: soap.niche,
                coreStrategy: soap.coreStrategy,
                enablers: soap.enablers,
                gaps: soap.criticalGaps.map((g: any) => g.title) // Map complex gap object to string array for now
            });

            // We might want to store the full Gap objects too, but for now this matches the interface
            console.log("SOAP Data Updated:", { ...soap, strategicAmbition, mission, vision });

        } catch (error) {
            console.error("SOAP Error:", error);
        } finally {
            setIsGeneratingSoap(false);
        }
    };

    const generateBlueprint = async (initiative: any) => {
        try {
            const companyContext = initiative.companyContext || { companyName: "Aura-Forge" };

            // DATA INTEGRITY FIX: Pass initiativeAnalysis to ensure consistent values
            // The operator should USE these values, not recalculate them
            const initiativeAnalysis = initiativeAnalyses[initiative.id] || null;
            
            console.log("📊 [generateBlueprint] Sending analysis for data consistency:", {
                initiativeId: initiative.id,
                hasAnalysis: !!initiativeAnalysis,
                revenueUpside: initiativeAnalysis?.revenueUpside?.value,
                budget: initiativeAnalysis?.budget?.total,
                fteCount: initiativeAnalysis?.budget?.fteCount
            });

            const response = await fetch("/api/strategy/blueprint", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    initiativeTitle: initiative.title,
                    companyContext,
                    // DATA INTEGRITY: Pass the analysis so operator uses consistent values
                    initiativeAnalysis: initiativeAnalysis ? {
                        revenueUpside: initiativeAnalysis.revenueUpside?.value,
                        ebitdaUpside: initiativeAnalysis.ebitdaUpside?.value,
                        budget: initiativeAnalysis.budget?.total,
                        fteCount: initiativeAnalysis.budget?.fteCount,
                        keyHires: initiativeAnalysis.budget?.keyHires,
                        duration: initiativeAnalysis.timeline?.duration,
                        irr: initiativeAnalysis.irr?.percentage,
                        yearlyImpacts: initiativeAnalysis.yearlyImpacts
                    } : null
                })
            });

            const result = await response.json();
            
            // Always try to use the blueprint data, even from non-200 responses
            // The API may return 422 (supervisor rejection) but still include usable blueprint data
            if (result.data) {
                if (result.supervisorReview && !result.supervisorReview.approved) {
                    console.warn("⚠️ Blueprint delivered with supervisor advisory:", result.supervisorReview.summaryForUser);
                }
                return result.data;
            }
            
            // Only throw if there's truly no data
            if (!response.ok) {
                throw new Error(result.error || result.meta?.message || "Blueprint Generation Failed");
            }
            
            return result.data;

        } catch (error) {
            console.error("Blueprint Error:", error);
            throw error;
        }
    };

    const generateDashboardInsights = async (foundationData: any, isDeepSearch: boolean = false, structuredFinancials?: any, formDataParam?: any) => {
        setIsGeneratingIntelligence(true);
        // Capture pinned content before refresh
        const currentPriorities = dashboardIntelligence?.strategicPriorities || [];
        const currentMarket = dashboardIntelligence?.marketIntelligence || [];
        const pinnedPriorities = currentPriorities.filter((p: any) => pinnedInsightIds.includes(p.title));
        const pinnedMarket = currentMarket.filter((m: any) => pinnedInsightIds.includes(m.title));

        try {
            // Get company config for data quality screening
            const companyConfig = activeCompany ? {
                reportingUnit: activeCompany.reportingUnit || 'M',
                reportingCurrency: activeCompany.reportingCurrency || 'USD'
            } : undefined;

            // CRITICAL: Pass structured financials for validated value extraction
            // This ensures the AI uses the same validated values as the UI widgets
            const response = await fetch("/api/dashboard/intelligence", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    foundationData,
                    soapData,
                    activeInitiatives,
                    isDeepSearch,
                    companyConfig,
                    structuredFinancials,
                    formData: formDataParam || foundationData
                })
            });

            if (!response.ok) throw new Error("Intelligence Generation Failed");
            const result = await response.json();
            const newData = result.data;

            // DEBUG: Log what we received from the API
            console.log("🔍 Dashboard Intelligence API Response:", {
                hasStrategicPriorities: !!newData.strategicPriorities,
                strategicPrioritiesCount: newData.strategicPriorities?.length || 0,
                strategicPriorities: newData.strategicPriorities,
                hasMarketIntelligence: !!newData.marketIntelligence,
                marketIntelligenceCount: newData.marketIntelligence?.length || 0,
                marketIntelligence: newData.marketIntelligence,
                performanceIndicators: newData.performanceIndicators?.length || 0
            });

            // Transform API response to match component schema
            // Strategic priorities may have: priority, urgency, impact, action OR be strings
            // Market intelligence may have: signal, impact, action OR be strings
            // Component expects: title, description, category, isUrgent

            const transformedPriorities = (newData.strategicPriorities || []).map((item: any) => {
                // If it's a string, use it as title
                if (typeof item === 'string') {
                    return { title: item, description: '', category: 'Strategic', hasNewBadge: true };
                }
                // If it already has the expected schema, use it as-is
                if (item.title && item.description) {
                    return item;
                }
                // Transform from API schema: create rich title and description
                const urgencyPrefix = item.urgency ? `${item.urgency}: ` : '';
                const title = item.priority || item.title || 'Unknown Priority';
                const description = item.impact || item.description || '';

                return {
                    title: `${urgencyPrefix}${title}`,
                    description: description,
                    category: item.urgency === 'High' || item.urgency === 'Critical' ? 'Risk' : 'Strategic',
                    isUrgent: item.urgency === 'High' || item.urgency === 'Critical',
                    action: item.action,
                    hasNewBadge: item.hasNewBadge !== undefined ? item.hasNewBadge : true
                };
            });

            const transformedMarket = (newData.marketIntelligence || []).map((item: any) => {
                // If it's a string, use it as title
                if (typeof item === 'string') {
                    return { title: item, description: '', category: 'Market', hasNewBadge: true };
                }
                // If it already has the expected schema, use it as-is
                if (item.title && item.description) {
                    return item;
                }
                // Transform from API schema
                const title = item.signal || item.title || 'Unknown Signal';
                const description = item.impact ? `${item.impact}${item.action ? ` What now? ${item.action}` : ''}` : (item.description || '');

                return {
                    title: title,
                    description: description,
                    category: 'Market',
                    action: item.action,
                    hasAction: !!item.action,
                    actionLabel: 'Explore implications',
                    hasNewBadge: item.hasNewBadge !== undefined ? item.hasNewBadge : true
                };
            });

            // ================================================================
            // SAFEGUARD: Filter out stale "data validation error" priorities
            // if the data quality score indicates no actual errors.
            // This prevents the AI from hallucinating a data error priority
            // when the underlying data has already been fixed.
            // ================================================================
            const dataQualityScore = newData.dataQualityScore || newData.validationScore || null;
            const dataValidationStatus = newData.dataValidationStatus || null;
            
            let filteredPriorities = transformedPriorities;
            if (dataValidationStatus === 'valid' || (dataQualityScore && dataQualityScore >= 90)) {
                // Remove any data/validation/discrepancy-related priorities since data is clean
                filteredPriorities = transformedPriorities.filter((p: any) => {
                    const text = `${p.title || ''} ${p.description || ''}`.toLowerCase();
                    const isDataValidationPriority = 
                        /data.*(?:validation|integrity|discrepanc|error|mismatch)/i.test(text) ||
                        /validation.*(?:status|error)/i.test(text) ||
                        /critical.*data.*discrepanc/i.test(text);
                    if (isDataValidationPriority) {
                        console.log(`🧹 [Safeguard] Filtering out stale data validation priority: "${p.title}" (validation status: ${dataValidationStatus}, score: ${dataQualityScore})`);
                    }
                    return !isDataValidationPriority;
                });
            }

            console.log("🔍 Transformed Data:", {
                transformedPriorities: filteredPriorities,
                transformedMarket,
                dataValidationStatus,
                dataQualityScore,
                filteredCount: transformedPriorities.length - filteredPriorities.length
            });

            // Merge pinned content back in
            // Filter new data to exclude items that conflict with pinned items (keep pinned version?) 
            // Or keep new version? User said "remain". Usually pinned implies "keep this exact one".
            // But if the agent updates data for the same title, maybe we want update? 
            // "Specific item should remain" -> implies exact content. I will keep pinned version.

            const mergedPriorities = [
                ...pinnedPriorities,
                ...filteredPriorities.filter((p: any) => !pinnedInsightIds.includes(p.title))
            ];

            const mergedMarket = [
                ...pinnedMarket,
                ...transformedMarket.filter((m: any) => !pinnedInsightIds.includes(m.title))
            ];

            setDashboardIntelligence({
                ...newData,
                strategicPriorities: mergedPriorities,
                marketIntelligence: mergedMarket
            });

        } catch (error) {
            console.error("Dashboard Intelligence Error:", error);
        } finally {
            setIsGeneratingIntelligence(false);
        }
    };

    const togglePinInsight = (id: string) => {
        setPinnedInsightIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const deleteInsight = (id: string) => {
        setDeletedInsightIds(prev => Array.from(new Set([...prev, id])));
        // If it was pinned, unpin it too
        setPinnedInsightIds(prev => prev.filter(i => i !== id));
    };

    // Pin initiative for later blueprint development
    const pinInitiative = (initiative: PinnedInitiative) => {
        setPinnedInitiatives(prev => {
            // Don't add duplicates
            if (prev.find(p => p.id === initiative.id)) return prev;
            return [...prev, { ...initiative, pinnedAt: new Date().toISOString() }];
        });
    };

    const unpinInitiative = (id: string) => {
        setPinnedInitiatives(prev => prev.filter(p => p.id !== id));
    };

    const isInitiativePinned = (id: string) => {
        return pinnedInitiatives.some(p => p.id === id);
    };
    
    // Initiative Analyses - SINGLE SOURCE OF TRUTH for financial data
    const setInitiativeAnalysis = (id: string, analysis: InitiativeAnalysisData) => {
        console.log(`📊 [StrategyContext] Setting analysis for ${id}:`, analysis.revenueUpside?.value, analysis.budget?.total);
        setInitiativeAnalysesState(prev => ({
            ...prev,
            [id]: analysis
        }));
    };
    
    const setInitiativeAnalyses = (analyses: Record<string, InitiativeAnalysisData>) => {
        console.log(`📊 [StrategyContext] Setting all analyses:`, Object.keys(analyses).length, "initiatives");
        setInitiativeAnalysesState(analyses);
    };
    
    const getInitiativeAnalysis = (id: string): InitiativeAnalysisData | undefined => {
        return initiativeAnalyses[id];
    };

    return (
        <StrategyContext.Provider
            value={{
                selectedInitiative,
                commitmentLevel,
                leadingKPIs,
                laggingKPIs,
                setSelectedInitiative,
                setCommitmentLevel,
                setLeadingKPIs,
                setLaggingKPIs,
                updateLeadingKPI,
                updateLaggingKPI,
                isDataIngested,
                activeInitiatives,
                setDataIngested,
                addActiveInitiative,
                removeActiveInitiative,
                soapData,
                updateSoapData,
                generateSoap,
                isGeneratingSoap,
                generateBlueprint,
                shortlistedPathways,
                toggleShortlistedPathway,
                isPathwayShortlisted,
                clearShortlistedPathways,
                updateShortlistedPathwayScores,
                selectedPathwayIds,
                setSelectedPathwayIds,
                blueprintGaps,
                setBlueprintGaps,
                selectedForBlueprint,
                setSelectedForBlueprint,
                pinnedInitiatives,
                pinInitiative,
                unpinInitiative,
                isInitiativePinned,
                dashboardIntelligence,
                generateDashboardInsights,
                isGeneratingIntelligence,
                setDashboardIntelligence,
                pinnedInsightIds,
                deletedInsightIds,
                togglePinInsight,
                deleteInsight,
                // Initiative analyses - SINGLE SOURCE OF TRUTH
                initiativeAnalyses,
                setInitiativeAnalysis,
                setInitiativeAnalyses,
                getInitiativeAnalysis
            }}
        >
            {children}
        </StrategyContext.Provider>
    );
};

export const useStrategy = () => {
    const context = useContext(StrategyContext);
    if (context === undefined) {
        throw new Error("useStrategy must be used within a StrategyProvider");
    }
    return context;
};
